import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Group,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Avatar,
  ActionIcon,
  Divider,
  LoadingOverlay,
} from "@mantine/core";
import {
  IconArrowRight,
  IconCheck,
  IconSearch,
  IconSparkles,
  IconStar,
  IconStarFilled,
  IconWorld,
} from "@tabler/icons-react";
import { getAgents } from "../../../../../api/AgentsAPI";
import { AVATARS } from "../../../ai-agent/agent/utils";
import { useCampaignStore } from "../../../../../stores/CampaignStore";

// --- HELPERS POUR MAPPER L'API ---
const formatApiDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
  }).format(date);
};

const extractCompany = (url) => {
  if (!url) return "Entreprise";
  let company = url
    .replace(/^https?:\/\/(www\.)?/, "")
    .split("/")[0]
    .split(".")[0];
  return company.charAt(0).toUpperCase() + company.slice(1);
};

const formatLanguage = (langCode) => {
  const map = { eng: "EN", fra: "FR", spa: "ES", deu: "DE", ita: "IT" };
  return map[langCode?.toLowerCase()] || langCode?.toUpperCase() || "EN";
};

// --- COMPOSANT PRINCIPAL ---
const StepAgent = ({ setActive }) => {
  const agentId = useCampaignStore((s) => s.agentId);
  const setAgentId = useCampaignStore((s) => s.setAgentId);
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // 'all', 'favorites', 'recent'

  // Filtrage logique
  const filteredAgents = useMemo(() => {
    let result = agents;

    // Filtre par recherche
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (a) =>
          a.name?.toLowerCase().includes(q) ||
          a.first_name?.toLowerCase().includes(q),
      );
    }

    // Filtre par onglet
    if (filter === "favorites") {
      result = result.filter((a) => a.is_favorite);
    }

    return result;
  }, [agents, search, filter]);

  const selectedAgent = agents.find((a) => a.uniqid === agentId);
  const selectedAgentName =
    selectedAgent?.first_name || selectedAgent?.name || "L'agent";
  const selectedAgentCompany = extractCompany(selectedAgent?.website_url);

  const handleNext = () => {
    setActive("theme");
  };

  useEffect(() => {
    getAgents()
      .then((res) => {
        setAgents(res.data.agents.sort((a, b) => b.id - a.id));
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box mx="auto" p="xl" pos={"relative"} miw={"100%"}>
      <LoadingOverlay visible={loading} />
      {/* HEADER */}
      <Stack gap={4} mb="xl">
        <Text c="violet.5" fw={600} size="md">
          Étape 1 · Votre copilote
        </Text>
        <Text component="h1" size="xl" fw={700} m={0} style={{ fontSize: 24 }}>
          Quel agent IA mènera cette campagne ?
        </Text>
        <Text c="dimmed">
          Chaque agent connaît déjà votre offre, votre marché et votre ton.
          Choisissez celui qui correspond à cette audience.
        </Text>
      </Stack>

      {/* CONTROLES : RECHERCHE & FILTRES */}
      <Group justify="space-between" align="center" mb="xl">
        <TextInput
          placeholder="Rechercher un agent"
          leftSection={<IconSearch size={18} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          radius="xl"
          size="md"
          w={{ base: "100%", sm: 400 }}
        />

        <Group
          gap={4}
          p={4}
          style={{
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: "999px",
            backgroundColor: "white",
          }}
        >
          {[
            { id: "all", label: "Tous" },
            { id: "favorites", label: "Favoris" },
            { id: "recent", label: "Récemment utilisés" },
          ].map((f) => {
            const active = filter === f.id;
            return (
              <Button
                key={f.id}
                variant={active ? "filled" : "subtle"}
                color={active ? "violet" : "gray"}
                radius="xl"
                size="sm"
                onClick={() => setFilter(f.id)}
                style={{
                  fontWeight: active ? 600 : 400,
                  color: active ? "white" : "var(--mantine-color-gray-6)",
                }}
              >
                {f.label}
              </Button>
            );
          })}
        </Group>
      </Group>

      {/* GRILLE DES AGENTS */}
      <Grid gutter="md" mb={40}>
        {filteredAgents.map((agent) => {
          const isSelected = agentId === agent.uniqid;
          const displayName = agent.first_name || agent.name;
          const company = extractCompany(agent.website_url);
          // Simulation multi-langues si besoin ("FR . EN")
          const langDisplay =
            formatLanguage(agent.spoken_language) +
            (agent.spoken_language === "fra" ? " . EN" : "");

          return (
            <Grid.Col span={{ base: 12, md: 6, lg: 3 }} key={agent.uniqid}>
              <Card
                withBorder
                radius="xl"
                p="lg"
                onClick={() => setAgentId(agent.uniqid)}
                style={{
                  cursor: "pointer",
                  borderColor: isSelected
                    ? "var(--mantine-color-violet-5)"
                    : "var(--mantine-color-gray-3)",
                  borderWidth: isSelected ? 2 : 1,
                  transition: "all 0.2s",
                  position: "relative",
                  overflow: "visible",
                }}
              >
                <Group
                  justify="space-between"
                  align="flex-start"
                  mb="md"
                  wrap="nowrap"
                >
                  <Group gap="sm" wrap="nowrap">
                    <Avatar
                      src={AVATARS.find((x) => x.id === agent.avatar)?.src}
                      size="lg"
                      radius="xl"
                    />
                    <Box>
                      <Text fw={700} size="lg" lh={1.2}>
                        {displayName}
                      </Text>
                      <Text size="sm" c="dimmed">
                        {agent.bio}
                      </Text>
                    </Box>
                  </Group>
                  <ActionIcon
                    variant="transparent"
                    color={agent.is_favorite ? "yellow.5" : "gray.4"}
                  >
                    {agent.is_favorite ? (
                      <IconStarFilled size={20} />
                    ) : (
                      <IconStar size={20} />
                    )}
                  </ActionIcon>
                </Group>

                <Text
                  size="md"
                  c="dark.3"
                  lineClamp={4}
                  style={{ minHeight: 72 }}
                  mb={8}
                >
                  {agent.bio || agent.description}. <br />
                  {agent.brief}
                </Text>

                <Divider mb="sm" color="gray.2" />

                <Group justify="space-between">
                  <Group gap={6}>
                    <IconWorld size={16} color="var(--mantine-color-gray-5)" />
                    <Text size="sm" c="dimmed" fw={500}>
                      {langDisplay}
                    </Text>
                  </Group>
                  <Text size="sm" c="dimmed" ml={"auto"}>
                    Créée le {formatApiDate(agent.created_on)}
                  </Text>
                  {isSelected && (
                    <ThemeIcon
                      size={24}
                      radius="xl"
                      color="primary"
                      style={{
                        border: "2px solid white",
                      }}
                      variant="filled"
                    >
                      <IconCheck size={14} stroke={3} />
                    </ThemeIcon>
                  )}
                </Group>
              </Card>
            </Grid.Col>
          );
        })}
      </Grid>

      {/* BOÎTE DE RÉSUMÉ DE SÉLECTION */}
      {selectedAgent && (
        <Card
          withBorder
          radius="xl"
          p="md"
          mb="xl"
          bg="violet.0"
          style={{ borderColor: "var(--mantine-color-violet-3)" }}
        >
          <Group wrap="nowrap" align="flex-start" gap="md">
            <Avatar
              src={AVATARS.find((x) => x.id === selectedAgent.avatar)?.src}
              size={64}
              radius="xl"
            />
            <Box>
              <Group gap={6} mb={4}>
                <IconSparkles size={18} color="var(--mantine-color-violet-5)" />
                <Text c="violet.5" fw={600} size="lg">
                  Agent sélectionné
                </Text>
              </Group>
              <Text size="md" c="dark.4" lh={1.5}>
                Vous êtes sur le point de lancer cette campagne avec{" "}
                <Text component="span" fw={700}>
                  {selectedAgentName}
                </Text>
                , {selectedAgent.bio}. {selectedAgentName} personnalisera chaque
                interaction en fonction de votre offre, de votre cible et des
                consignes que vous lui avez transmises.
              </Text>
            </Box>
          </Group>
        </Card>
      )}

      {/* FOOTER / CONTINUER */}
      <Group justify="flex-end" mt="xl">
        <Button
          id="agent-submit"
          color="violet"
          radius="xl"
          size="md"
          rightSection={<IconArrowRight size={18} />}
          disabled={!agentId}
          onClick={handleNext}
        >
          Continuer
        </Button>
      </Group>
    </Box>
  );
};

export default StepAgent;
